/**
 * The filters over cells that reference something else: select options, linked
 * rows, and users.
 *
 * What they share is that the filter value is an id (or a list of ids) rather
 * than text, and that a filter value which is not an id abstains. That is what
 * makes a freshly added filter show every row instead of none while its
 * dropdown is still empty.
 */
import { stringify } from './handlers'
import type { MatchResult } from './types'

/** Whether a string is a plain integer, as the id-valued filters require. */
function isIntegerText(value: string): boolean {
  return /^-?\d+$/.test(value)
}

/** The `id` of a select-option or user cell, if it has one. */
function idOf(value: unknown): number | null {
  if (value === null || typeof value !== 'object') return null
  const id = (value as { id?: unknown }).id
  return typeof id === 'number' ? id : null
}

/**
 * Reads a comma-separated list of ids.
 *
 * Null when the filter is empty, which every caller treats as abstaining.
 * Upstream parses this with the CSV reader, which additionally understands
 * quoting -- irrelevant here, because the only writer of this value is a
 * multi-select dropdown that emits bare integers.
 */
export function parseIdList(value: unknown): number[] | null {
  if (value === null || value === undefined || value === '') return null
  const ids = stringify(value)
    .split(',')
    .map((part) => parseInt(part, 10))
  return [...new Set(ids)]
}

/* ------------------------------------------------------------ select options */

export function matchesSingleSelectEqual(
  rowValue: unknown,
  filterValue: string,
): MatchResult {
  if (filterValue === '') return null
  return idOf(rowValue) === parseInt(filterValue, 10)
}

/**
 * Whether the option is not the one chosen.
 *
 * An empty cell counts as "not equal" -- a row with nothing selected is not the
 * option being excluded, so it stays in the view.
 */
export function matchesSingleSelectNotEqual(
  rowValue: unknown,
  filterValue: string,
): MatchResult {
  if (filterValue === '') return null
  const id = idOf(rowValue)
  return id === null || id !== parseInt(filterValue, 10)
}

export function matchesSingleSelectIsAnyOf(
  rowValue: unknown,
  filterValue: string,
): MatchResult {
  const ids = parseIdList(filterValue)
  if (ids === null) return true
  const id = idOf(rowValue)
  return id !== null && ids.includes(id)
}

export function matchesSingleSelectIsNoneOf(
  rowValue: unknown,
  filterValue: string,
): MatchResult {
  const ids = parseIdList(filterValue)
  if (ids === null) return true
  const id = idOf(rowValue)
  return id === null || !ids.includes(id)
}

export function matchesMultipleSelectHas(
  rowValue: unknown,
  filterValue: string,
): MatchResult {
  const ids = parseIdList(filterValue)
  if (ids === null) return true
  if (!Array.isArray(rowValue) || rowValue.length === 0) return false
  return rowValue.some((option) => {
    const id = idOf(option)
    return id !== null && ids.includes(id)
  })
}

export function matchesMultipleSelectHasNot(
  rowValue: unknown,
  filterValue: string,
): MatchResult {
  const ids = parseIdList(filterValue)
  if (ids === null) return true
  if (!Array.isArray(rowValue) || rowValue.length === 0) return true
  return rowValue.every((option) => {
    const id = idOf(option)
    return id === null || !ids.includes(id)
  })
}

/* ------------------------------------------------------------------- users */

/**
 * Whether a user-valued cell names a particular person.
 *
 * `user_is` and `user_is_not` compare an id, so the viewer's own identity does
 * not enter into it -- "is me" is expressed by the filter carrying the viewer's
 * id, which the UI fills in when the filter is created.
 */
export function matchesUserIs(rowValue: unknown, filterValue: string): MatchResult {
  if (!isIntegerText(filterValue)) return true
  return idOf(rowValue) === parseInt(filterValue, 10)
}

export function matchesUserIsNot(rowValue: unknown, filterValue: string): MatchResult {
  if (!isIntegerText(filterValue)) return true
  return idOf(rowValue) !== parseInt(filterValue, 10)
}

export function matchesCollaboratorsHas(
  rowValue: unknown,
  filterValue: string,
): MatchResult {
  if (!isIntegerText(filterValue)) return true
  if (!Array.isArray(rowValue)) return false
  const wanted = parseInt(filterValue, 10)
  return rowValue.some((user) => idOf(user) === wanted)
}

export function matchesCollaboratorsHasNot(
  rowValue: unknown,
  filterValue: string,
): MatchResult {
  if (!isIntegerText(filterValue)) return true
  if (!Array.isArray(rowValue)) return true
  const wanted = parseInt(filterValue, 10)
  return !rowValue.some((user) => idOf(user) === wanted)
}

/* --------------------------------------------------------------- link rows */

export function matchesLinkRowHas(rowValue: unknown, filterValue: string): MatchResult {
  if (!isIntegerText(filterValue)) return true
  if (!Array.isArray(rowValue)) return false
  const wanted = parseInt(filterValue, 10)
  return rowValue.some((relation) => idOf(relation) === wanted)
}

export function matchesLinkRowHasNot(
  rowValue: unknown,
  filterValue: string,
): MatchResult {
  if (!isIntegerText(filterValue)) return true
  if (!Array.isArray(rowValue)) return true
  const wanted = parseInt(filterValue, 10)
  return !rowValue.some((relation) => idOf(relation) === wanted)
}

/**
 * Whether any linked row's text contains the filter value.
 *
 * Upstream builds `new RegExp(filterValue, 'i')` from the raw input, so a filter
 * value containing `(` throws a SyntaxError -- which aborts the whole row-match
 * pass, not just this filter -- and one containing `.` or `*` silently matches
 * rows it should not. A plain case-insensitive substring test is what the
 * regex was reaching for, and is what the backend does.
 */
function linkRowTextMatches(rowValue: unknown, filterValue: string): boolean {
  if (!Array.isArray(rowValue)) return false
  const needle = filterValue.toLowerCase()
  return rowValue.some((relation) => {
    const text = (relation as { value?: unknown }).value
    return stringify(text).toLowerCase().includes(needle)
  })
}

export function matchesLinkRowContains(
  rowValue: unknown,
  filterValue: string,
): MatchResult {
  if (filterValue === '') return true
  return linkRowTextMatches(rowValue, filterValue)
}

export function matchesLinkRowNotContains(
  rowValue: unknown,
  filterValue: string,
): MatchResult {
  if (filterValue === '') return true
  return !linkRowTextMatches(rowValue, filterValue)
}
